import { Container, Alert, Modal, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { NavBar } from "../navbar/navbar";
import { useEffect, useMemo, useState, useCallback, useRef, Suspense, lazy } from "react";
import { useApi } from "./apiService";
import { SignalsList } from "./signals/SignalsList";
import { DailySummaryCard } from "./signals/DailySummaryCard";
import { GridMix } from "./grid/GridMix";
import { AreaPicker } from "./AreaPicker";
import { Chart } from "./chart/chart";
import { track } from "../../utils/track";
import { CurrentPrice } from "./currentPrice/currentPrice";
import {
  ALL_AREA_CODES,
  areasInCountry,
  countryOf,
} from "./constants";
import { Price } from "../models/price";
import "./home.css";
import { useLanguage } from "../../i18n/LanguageProvider";
import { useCurrency } from "../../currency/CurrencyProvider";
import { Prediction } from "./chart/chartTypes";
import { useUrlState } from "../../hooks/useUrlState";
import { usePermissions } from "../../hooks/usePermissions";
import { readViewPreference, writeViewPreference } from "../../utils/viewPreference";

// MapLibre is heavy: the home-page zone map loads in its own chunk.
const ZoneMiniMap = lazy(() => import("../map/ZoneMiniMap"));

// Electricity is the only asset (with SE bidding zones).
type AssetType = 'electricity';

const VALID_ASSETS: AssetType[] = ['electricity'];
// Valid electricity zone selections: a single bidding zone (SE1–SE4).
const DEFAULT_AREA = 'SE3';

const Home = () => {
  const api = useApi();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { getParam, setParam, setParams } = useUrlState();
  const { hasPermission } = usePermissions();

  // Permission checks for different resource types
  const canViewElectricity = hasPermission('electricity');
  // Entitlement for the 30-day extended (daily) forecast horizon.
  const canViewExtended = hasPermission('forecasts', 'extended');

  // Last-used view (zone/range), read once. Restores the user's usual screen on a
  // bare visit (see the hydration effect below); once any param is in the URL it
  // is the sole source of truth, so explicit/shared links always win.
  const savedView = useMemo(() => readViewPreference(), []);

  // Forecast range, URL-synced (?range=30d) for shareable links. Forced to 7d
  // without the extended grant.
  const urlRange = getParam('range');
  const forecastRange: '7d' | '30d' =
    canViewExtended && urlRange === '30d' ? '30d' : '7d';
  const setForecastRange = useCallback((range: '7d' | '30d') => {
    setParam('range', range === '30d' ? '30d' : null);
  }, [setParam]);

  const [prices, setPrices] = useState<Record<string, Price[]>>({});
  const [rawPredictions, setRawPredictions] = useState<Record<string, Prediction[]>>({});
  const [loadingPrices, setLoadingPrices] = useState(true);
  const [loadingPredictions, setLoadingPredictions] = useState(true);
  // Set when price/prediction fetches fail (network/server) so we can show the
  // user a banner instead of silently rendering an empty chart.
  const [loadError, setLoadError] = useState(false);
  const [availableTimestamps, setAvailableTimestamps] = useState<string[]>([]);
  const [loadingTimestamps, setLoadingTimestamps] = useState(true);
  const [selectedGeneratedAt, setSelectedGeneratedAt] = useState<string | null>(null);
  const [showHistoricalPrices, setShowHistoricalPrices] = useState(false);

  // URL-synced state for active asset/chart type. Electricity is the only asset.
  const urlChart = getParam('chart');

  // Electricity is the only asset, gated on the electricity permission.
  const permittedAssets = useMemo<AssetType[]>(
    () => (canViewElectricity ? ['electricity'] : []),
    [canViewElectricity],
  );

  const activeAsset: AssetType | null = canViewElectricity ? 'electricity' : null;

  // Keep 'commodity' as the prop name for backward compatibility with child components
  const commodity = activeAsset as AssetType;

  // Electricity zone (SE1–4), URL-synced (?area=SE3). One zone at a time; SE3 is
  // the default. ('ALL' was removed — grid/fundamentals data is per zone.)
  const urlArea = getParam('area');
  const selectedArea =
    urlArea && ALL_AREA_CODES.includes(urlArea) ? urlArea : DEFAULT_AREA;
  const setSelectedArea = useCallback((zone: string) => {
    setParam('area', zone);
  }, [setParam]);

  // Zones fetched and listed together: the selected area's country (SE1–SE4
  // unless the multi-country preview is on).
  const country = countryOf(selectedArea);
  const areaCodes = useMemo(() => areasInCountry(selectedArea), [selectedArea]);

  // Forecast model: the standard one, or the Pro forecast (?model=pro) for
  // Premium (forecasts:pro) and admins. Pro is hourly over 7 days, so it ignores 30d.
  const canViewPro = hasPermission('forecasts', 'pro') || hasPermission('analytics', 'read');
  const urlModel = getParam('model');
  const forecastModel: string | null = canViewPro && urlModel === 'pro' ? 'pro' : null;
  const setForecastModel = useCallback((model: string | null) => {
    setParam('model', model);
  }, [setParam]);
  const effectiveRange: '7d' | '30d' = forecastModel ? '7d' : forecastRange;

  // Layout is two independent CSS columns (see .home-cols in home.css): price +
  // signals on the left, chart + mix/summary on the right, each at its own height.
  // No JS height measurement needed.

  // On first load, hydrate the URL once from the saved view. Each param is
  // restored independently only when that specific param is ABSENT from the URL,
  // so an explicitly-set / shared param always wins, but a partial URL (e.g.
  // ?chart=electricity with no ?area) still restores the missing pieces — the
  // common case when navigating in from another page, where ?chart lingers but
  // ?area does not. Must be a plain useEffect (not useLayoutEffect / render-time):
  // react-router drops a navigate() issued during the first commit ("call
  // navigate() in a useEffect"), which silently dropped the restore write.
  // Mount-once (guarded) means the user can freely switch to ALL/7d afterward
  // without it snapping back to the saved value. `hydrated` is still false on the
  // first render, so the persist effect below won't clobber the saved value
  // before this restore lands.
  const hydratedRef = useRef(false);
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;
    const patch: Record<string, string | null> = {};
    if (urlChart === null && savedView.chart && permittedAssets.includes(savedView.chart as AssetType)) {
      patch.chart = savedView.chart;
    }
    if (urlArea === null && savedView.area && ALL_AREA_CODES.includes(savedView.area)) {
      patch.area = savedView.area;
    }
    if (urlRange === null && canViewExtended && savedView.range === '30d') {
      patch.range = '30d';
    }
    // Ensure the asset is in the URL unless the saved restore above already set it.
    if (patch.chart === undefined && urlChart === null && activeAsset) {
      patch.chart = activeAsset;
    }
    if (Object.keys(patch).length > 0) setParams(patch, { replace: true });
    setHydrated(true);
  }, [urlChart, urlArea, urlRange, savedView, permittedAssets, canViewExtended, activeAsset, setParams]);

  // Persist the current view so the next bare visit lands here. Gated on `hydrated`
  // so it never fires on the pre-hydration render — otherwise the default
  // (electricity/ALL/7d) would clobber the saved preference in localStorage before
  // the hydration effect restores it, making every bare visit reset to the default.
  useEffect(() => {
    if (!hydrated || !activeAsset) return;
    writeViewPreference({ chart: activeAsset, area: selectedArea, range: forecastRange });
  }, [hydrated, activeAsset, selectedArea, forecastRange]);

  // Prices and forecasts are stored in SEK/kWh; convert once for display.
  const { convert, currency } = useCurrency();
  const displayPrices = useMemo(() => {
    if (currency === 'SEK') return prices;
    const out: Record<string, Price[]> = {};
    for (const [area, ps] of Object.entries(prices)) out[area] = ps.map((p) => ({ ...p, value: convert(p.value) }));
    return out;
  }, [prices, convert, currency]);
  const displayPredictions = useMemo(() => {
    if (currency === 'SEK') return rawPredictions;
    const out: Record<string, Prediction[]> = {};
    for (const [area, ps] of Object.entries(rawPredictions)) {
      out[area] = ps.map((p) => ({ ...p, predictedValue: convert(p.predictedValue) }));
    }
    return out;
  }, [rawPredictions, convert, currency]);

  // Guards prediction fetches against out-of-order responses (see below).
  const predReqRef = useRef(0);

  const fetchPrices = async () => {
    setLoadingPrices(true);
    setLoadError(false);
    try {
      const pricesData: Record<string, Price[]> = {};

      // Get today's date range. End at the start of the day AFTER tomorrow so the
      // request includes all of tomorrow's day-ahead spot prices (published ~13:00
      // CET). Once those exist the chart's "forecast starts here" line advances a
      // full day automatically (it anchors on the last actual price).
      const today = new Date();
      const startTime = new Date(today.getFullYear(), today.getMonth(), today.getDate() -30).toISOString();
      const endTime = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 2).toISOString();

      for (const area of areaCodes) {
        try {
          const priceResponse = await api.fetchElectricityPrices(startTime, endTime, area);
          if (priceResponse.prices) {
            pricesData[area] = priceResponse.prices;
          }
        } catch (error) {
          console.error(`Error fetching prices for ${area}:`, error);
          setLoadError(true);
        }
      }

      setPrices(pricesData);
    } catch (error) {
      console.error("Error fetching prices:", error);
      setLoadError(true);
    } finally {
      setLoadingPrices(false);
    }
  };

  const fetchTimestamps = async () => {
    setLoadingTimestamps(true);
    try {
      // For electricity, timestamps are the same across areas - just fetch for one
      const res = await api.fetchElectricityTimestamps(areaCodes[0], forecastModel);
      const timestamps: string[] = res.timestamps || [];

      setAvailableTimestamps(timestamps);
      // Auto-select latest (first in desc-sorted list). The predictions fetch is
      // owned by the [selectedGeneratedAt, effectiveRange] effect below — fetching
      // here too raced the hydration-driven range flip (7d→30d on first load),
      // and whichever response landed last won, so the chart could end up showing
      // the wrong (hourly vs daily) shape and an empty near-term band until reload.
      if (timestamps.length > 0) {
        setSelectedGeneratedAt(timestamps[0]);
      } else {
        setSelectedGeneratedAt(null);
        setRawPredictions({});
        setLoadingPredictions(false);
      }
    } catch (error) {
      console.error("Error fetching timestamps:", error);
      setLoadError(true);
    } finally {
      setLoadingTimestamps(false);
    }
  };

  const fetchPredictionsForTimestamp = async (generatedAt: string) => {
    // Monotonic request id: overlapping fetches (e.g. the range flipping while an
    // earlier fetch is still in flight) can resolve out of order. Only the latest
    // request is allowed to write state, so the chart never renders a stale shape.
    const reqId = ++predReqRef.current;
    setLoadingPredictions(true);
    try {
      const predictions: Record<string, Prediction[]> = {};

      // Electricity: fetch the country's areas in parallel
      const results = await Promise.all(
        areaCodes.map(area =>
          api.fetchElectricityPredictions(area, generatedAt, effectiveRange, forecastModel)
            .then(res => ({ area, predictions: res.predictions || [] }))
            .catch(error => {
              console.error(`Error fetching predictions for ${area}:`, error);
              if (reqId === predReqRef.current) setLoadError(true);
              return { area, predictions: [] };
            })
        )
      );
      if (reqId !== predReqRef.current) return; // superseded by a newer request
      results.forEach(({ area, predictions: preds }) => {
        predictions[area] = preds.map((p: any) => ({
          ...p,
          area: p.area || area,
        }));
      });

      setRawPredictions(predictions);
    } catch (error) {
      console.error("Error fetching predictions:", error);
      if (reqId === predReqRef.current) setLoadError(true);
    } finally {
      if (reqId === predReqRef.current) setLoadingPredictions(false);
    }
  };

  // Load prices + predictions on mount.
  useEffect(() => {
    if (!activeAsset) return; // No permissions — skip all fetches
    setLoadingPrices(true);
    setLoadingTimestamps(true);
    setLoadingPredictions(true);
    fetchPrices();
    fetchTimestamps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commodity, country, forecastModel]);

  // Single owner of the predictions fetch: fires when the selected vintage is
  // first set (after timestamps load) and whenever the 7d/30d range toggles (the
  // two modes return different shapes — hourly vs daily-aggregated — from the
  // server). Keying on both, instead of also fetching inside fetchTimestamps,
  // removes the first-load race with the hydration-driven range flip.
  useEffect(() => {
    if (selectedGeneratedAt) {
      fetchPredictionsForTimestamp(selectedGeneratedAt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGeneratedAt, effectiveRange, country, forecastModel]);

  if (!activeAsset) {
    return (
      <>
        <NavBar />
        <Container fluid className="home-container">
          <div className="page-header">
            <div className="page-header__content">
              <h1 className="page-header__title">{t('home.title')}</h1>
              <p className="page-header__subtitle">{t('home.subtitle')}</p>
            </div>
          </div>
          <Alert variant="info" className="mt-3">
            {t('home.noAccess')}
          </Alert>
        </Container>
      </>
    );
  }

  return (
    <>
      <NavBar
        centerSlot={
          <AreaPicker
            selectedArea={selectedArea}
            onChange={setSelectedArea}
            model={forecastModel}
            onModelChange={setForecastModel}
            canViewPro={canViewPro}
            onUpgrade={() => navigate('/account?upgrade=1')}
            showCurrency
          />
        }
      />
      <Container fluid className="home-container">
        {loadError && (
          <Alert
            variant="warning"
            className="d-flex align-items-center justify-content-between mb-2 mb-md-3"
          >
            <span>{t("home.loadError")}</span>
            <Button
              size="sm"
              variant="outline-dark"
              onClick={() => {
                fetchPrices();
                fetchTimestamps();
              }}
            >
              {t("home.retry")}
            </Button>
          </Alert>
        )}
        {/* Two independent columns at lg+ (see .home-cols in home.css). Left holds
            the zone map, current price and market signals; right holds the
            forecast chart with production mix (2/6) + summary (4/6) beneath it.
            Below lg it all stacks: map → price → signals → chart → mix → summary. */}
        <div className="home-cols">
          {/* Left column: zone map, current price, market signals (filling the rest). */}
          <div className="home-col-left">
            <Suspense fallback={null}>
              <ZoneMiniMap api={api} selectedArea={selectedArea} onSelectArea={setSelectedArea} />
            </Suspense>
            <CurrentPrice
              prices={displayPrices}
              loadingPrices={loadingPrices}
              commodity={commodity}
              selectedArea={selectedArea}
              onExpand={() => { setShowHistoricalPrices(true); track('modal_open', 'historical_prices'); }}
            />
            <div className="home-signals-slot">
              <div className="home-signals-abs">
                <SignalsList api={api} />
              </div>
            </div>
          </div>
          {/* Right column: forecast chart, then production mix (2/6) + daily summary (4/6). */}
          <div className="home-col-right">
            <Chart
              rawPredictions={displayPredictions}
              loadingPredictions={loadingPredictions}
              prices={displayPrices}
              loadingPrices={loadingPrices}
              commodity={commodity}
              availableTimestamps={availableTimestamps}
              loadingTimestamps={loadingTimestamps}
              selectedGeneratedAt={selectedGeneratedAt}
              forecastRange={effectiveRange}
              onForecastRangeChange={setForecastRange}
              canViewExtended={canViewExtended && !forecastModel}
              selectedArea={selectedArea}
              onAreaChange={setSelectedArea}
            />
            <div className="home-bottom-row">
              <div className="home-mix-slot">
                <GridMix area={selectedArea} />
              </div>
              <div className="home-summary-slot">
                <DailySummaryCard api={api} />
              </div>
            </div>
          </div>
        </div>
      </Container>

      <Modal
        show={showHistoricalPrices}
        onHide={() => setShowHistoricalPrices(false)}
        size="xl"
        centered
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>{t('home.historicalPrices')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <CurrentPrice prices={displayPrices} loadingPrices={loadingPrices} commodity={commodity} selectedArea={selectedArea} />
        </Modal.Body>
      </Modal>
    </>
  );
}

export { Home };
